import { forwardRef, useId } from 'react';
import type { CompletionPosterModel } from './completionPosterModel';

const ticket = 'M48 3H722Q767 3 767 48V861C719 861 719 937 767 937V1180Q767 1225 722 1225H48Q3 1225 3 1180V937C51 937 51 861 3 861V48Q3 3 48 3Z';
const short = (text: string, length: number) => [...text].length > length ? [...text].slice(0, length - 1).join('') + '…' : text;

/** Real SVG text and a real QR share the same ticket geometry in the dialog and exported PNG. */
const CompletionSharePoster = forwardRef<SVGSVGElement, { model: CompletionPosterModel; qr: string }>(
  function CompletionSharePoster({ model, qr }, ref) {
    const id = useId().replaceAll(':', '');
    const surface = id + '-surface', blue = id + '-blue', panel = id + '-panel', clip = id + '-ticket';
    return <svg ref={ref} className="share-poster share-completion-poster" xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 770 1228" role="img" aria-label="练刻训练完成凭证" fontFamily="Microsoft YaHei, PingFang SC, sans-serif">
      <title>练刻训练完成凭证</title>
      <desc>{model.headline}，{model.dayName}，{model.metrics.map(m => m.value + m.unit).join('，')}。{model.rows.map(r => r.name + '：' + r.detail).join('；')}</desc>
      <defs>
        <linearGradient id={surface} x1="0" y1="0" x2="1" y2="1">
          <stop stopColor="#edf4fa"/><stop offset=".25" stopColor="#fbfdff"/><stop offset=".52" stopColor="#e8f0f8"/>
          <stop offset=".75" stopColor="#f6faff"/><stop offset="1" stopColor="#dfeaf5"/>
        </linearGradient>
        <linearGradient id={blue} x1="0" y1="0" x2=".7" y2="1">
          <stop stopColor="#60c0ff"/><stop offset=".35" stopColor="#1697ff"/><stop offset="1" stopColor="#0074ff"/>
        </linearGradient>
        <linearGradient id={panel} x1="0" y1="0" x2="1" y2="1">
          <stop stopColor="#dce7f1"/><stop offset=".45" stopColor="#f2f7fc"/><stop offset="1" stopColor="#d9e4ee"/>
        </linearGradient>
        <clipPath id={clip}><path d={ticket}/></clipPath>
        <filter id={id + '-inset'} x="-10%" y="-10%" width="120%" height="130%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#708ba5" floodOpacity=".16"/>
        </filter>
      </defs>
      <g clipPath={`url(#${clip})`}>
        <path d={ticket} fill={`url(#${surface})`}/>
        <ellipse cx="275" cy="320" rx="430" ry="460" fill="#ffffff" opacity=".23"/>
      </g>
      <path d={ticket} fill="none" stroke="#c2d5e8" strokeWidth="2"/>
      <path d={ticket} fill="none" stroke="#ffffff" strokeWidth="3" transform="translate(4 4) scale(.989 .993)"/>
      <g fill="#07183f">
        <text x="47" y="80" fontSize="40" fontWeight="900" fontStyle="italic">练刻</text>
        <text x="719" y="76" textAnchor="end" fontSize="27" fill="#6d85a4">{model.date}</text>
        <text x="45" y="204" fontSize="66" fontWeight="900" letterSpacing="-2">{model.headline}</text>
        <text x="52" y="267" fontSize="34" fill="#5f789b">{short(model.dayName, 15)}</text>
      </g>
      <g transform="translate(667 191)">
        <circle r="77" fill={`url(#${blue})`} stroke="#3f9fff" strokeWidth="2"/>
        <circle r="69" fill="none" stroke="#ffffff" strokeWidth="3"/>
        <circle r="62" fill="none" stroke="#ffffff" strokeWidth="1.5" opacity=".85"/>
        <path d="M-23 -21L-8 -7L22 -41" fill="none" stroke="white" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round"/>
        <text y="36" fill="white" fontSize={model.badge.length > 2 ? 28 : 34} fontWeight="900" textAnchor="middle" transform="rotate(-10)">{model.badge}</text>
      </g>
      {model.metrics.map((metric, index) => <g key={metric.unit} transform={`translate(${150 + index * 235} 370)`} textAnchor="middle">
        <text fill="#0085ff" fontSize="73" fontWeight="900">{metric.value}</text>
        <text y="43" fill="#5e789c" fontSize="29">{metric.unit}</text>
      </g>)}
      <path d="M267 325V407M502 325V407" stroke="#aebfd4"/>
      <rect x="43" y="445" width="684" height="287" rx="20" fill={`url(#${panel})`} stroke="#bcd0e5" filter={`url(#${id}-inset)`}/>
      <rect x="44" y="446" width="682" height="285" rx="19" fill="none" stroke="white" strokeOpacity=".8"/>
      {model.rows.map((row, index) => <g key={index} fill="#07183f">
        <text x="82" y={518 + index * 84} fontSize="33" fontWeight="600" aria-label={row.name}>{short(row.name, 10)}</text>
        <text x="686" y={518 + index * 84} textAnchor="end" fontSize={row.detail.length > 14 ? 26 : 32}>{row.detail}</text>
        {index < model.rows.length - 1 && <path d={`M82 ${549 + index * 84}H686`} stroke="#b9cadc"/>}
      </g>)}
      {model.rows.length === 0 && <text x="385" y="592" textAnchor="middle" fill="#5f789b" fontSize="29">训练数据已记录</text>}
      <text x="385" y="765" fill="#5d7799" fontSize="23" textAnchor="middle">{model.listCaption}</text>
      <text x="385" y="848" fill="#07183f" fontSize="48" fontWeight="900" textAnchor="middle">{model.caption}</text>
      <path d="M58 900H712" stroke="#a2b9d3" strokeWidth="7" strokeDasharray="0 18" strokeLinecap="round"/>
      <rect x="307" y="932" width="156" height="156" rx="16" fill="white" stroke="#d4e3f3"/>
      <image href={qr} x="316" y="941" width="138" height="138" imageRendering="pixelated"/>
      <text x="385" y="1121" fill="#07183f" fontSize="26" textAnchor="middle">扫码，一起开练</text>
      <text x="385" y="1152" fill="#68819f" fontSize="24" textAnchor="middle">owlbuy.cn</text>
      <path d="M102 1191H219M551 1191H668" stroke="#98aec6"/>
      <text x="385" y="1197" fill="#6683a6" fontSize="15" letterSpacing="6" textAnchor="middle">{model.footer}</text>
    </svg>;
  });
export default CompletionSharePoster;
